import React, { useEffect, useState } from 'react';
import {
  AuthFieldContainer,
  AuthFieldLabel,
  AuthFormContainer,
  AuthFormLink,
  AuthInputField,
  AuthSubmitButton,
  FlexAuthFieldLabel,
} from '../LoginPagesUI';
import FieldError from '../../../../components/Form/FieldError';
import {
  ICognitoChallenge,
  ICognitoResponse,
  ILoginInput,
  LoginStep,
} from '../types';
import { Updater } from 'use-immer';
import Cognito from '../../utils/Cognito';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { userLoggedInSuccessfully } from '../../../../utils/analytics/events/user/events';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import TosMessage from './TosMessage';
import AuthFormPasswordField from './AuthFormPasswordField';

interface IProps {
  setLoginInput: Updater<ILoginInput>;
  setLoginStep: (step: LoginStep) => void;
  loginInput: ILoginInput;
}

const LoginForm = ({ setLoginInput, loginInput, setLoginStep }: IProps) => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const onInputChange = (value: string, key: keyof ILoginInput) => {
    setHasError(false);
    setLoginInput((draft) => {
      draft[key] = value;
    });
  };

  /**
   * handle login button click
   * if return 200, navigate to the home page
   * if return challenge response, handle the challenge by diff cases
   *    and render diff challenge steps
   */
  const handleLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    Cognito.usernameLogin(loginInput.email, loginInput.password)
      .then(() => {
        track(userLoggedInSuccessfully());
        navigate('/');
      })
      .catch((error: AxiosError<ICognitoResponse>) => {
        setIsSubmitting(false);
        const challenge = error.response?.data?.challenge;
        switch (challenge) {
          case ICognitoChallenge.NewPasswordRequired:
            setLoginStep(LoginStep.ChangePassword);
            return;
          case ICognitoChallenge.MfaSetup:
            setLoginStep(LoginStep.MfaSetup);
            return;
          case ICognitoChallenge.MfaRequired:
            setLoginStep(LoginStep.MfaVerify);
            return;
          default:
            console.error('Login Error: ', error.response?.data);
            setHasError(true);
            return;
        }
      });
  };

  // if token is still valid, it means user manually enter the /login url
  // then redirect user to the homepage.
  useEffect(() => {
    setIsSubmitting(true);
    Cognito.refreshToken()
      .then(() => {
        navigate('/');
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  }, [navigate]);

  return (
    <AuthFormContainer>
      <AuthFieldContainer>
        <AuthFieldLabel>Email</AuthFieldLabel>
        <AuthInputField
          type="text"
          placeholder={'Enter your email'}
          value={loginInput.email}
          onChange={(value) => onInputChange(value, 'email')}
          autoComplete={'username'}
        />
      </AuthFieldContainer>
      <AuthFieldContainer>
        <FlexAuthFieldLabel>
          Password
          <AuthFormLink
            type="button"
            onClick={() => setLoginStep(LoginStep.ForgotPassword)}
          >
            Forgot password?
          </AuthFormLink>
        </FlexAuthFieldLabel>
        <AuthFormPasswordField
          placeholder={'Enter your password'}
          value={loginInput.password}
          onChange={(value) => onInputChange(value, 'password')}
          autoComplete={'current-password'}
        />
      </AuthFieldContainer>
      {hasError && (
        <FieldError
          type="custom"
          customError={'Email or password is not correct'}
        />
      )}
      <AuthSubmitButton
        type="submit"
        size="default"
        loading={isSubmitting}
        onClick={handleLogin}
        trackContext="login"
        trackAction="password login - log in"
      >
        Login now
      </AuthSubmitButton>
      <TosMessage />
    </AuthFormContainer>
  );
};

export default LoginForm;
